/**
 * useAds — read-only Meta ads mirror. Reads marketing.meta_campaign +
 * meta_ad populated by the meta-ads-fetch-all daily cron. No boost or
 * pause/resume actions — that's done in Meta Ads Manager directly.
 */
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface Campaign {
  id: string;
  name: string | null;
  objective: string | null;
  status: string | null;
  effective_status: string | null;
  daily_budget_inr: number | null;
  lifetime_budget_inr: number | null;
  start_time: string | null;
  stop_time: string | null;
  created_time: string | null;
  updated_time: string | null;
  spend_inr: number | null;
  impressions: number | null;
  reach: number | null;
  clicks: number | null;
  cpm: number | null;
  cpc: number | null;
  ctr: number | null;
  synced_at: string;
}

export interface Ad {
  id: string;
  campaign_id: string | null;
  adset_id: string | null;
  name: string | null;
  status: string | null;
  effective_status: string | null;
  ig_media_id: string | null;
  ig_permalink: string | null;
  ig_thumbnail_url: string | null;
  created_time: string | null;
  updated_time: string | null;
  spend_inr: number | null;
  impressions: number | null;
  reach: number | null;
  clicks: number | null;
  cpm: number | null;
  cpc: number | null;
  ctr: number | null;
  synced_at: string;
}

export function useCampaigns(limit = 50) {
  return useQuery<Campaign[]>({
    queryKey: ['ads-campaigns', limit],
    queryFn: async () => {
      const { data, error } = await marketing
        .from('meta_campaign')
        .select('*')
        .order('updated_time', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Campaign[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useAdsForCampaign(campaignId: string | null) {
  return useQuery<Ad[]>({
    queryKey: ['ads-list', campaignId],
    enabled: !!campaignId,
    queryFn: async () => {
      if (!campaignId) return [];
      const { data, error } = await marketing
        .from('meta_ad')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('spend_inr', { ascending: false, nullsFirst: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as Ad[];
    },
    staleTime: 5 * 60_000,
  });
}

export async function refreshAdsSync(): Promise<{ ok: boolean; error?: string; campaigns_synced?: number; ads_synced?: number }> {
  const r = await fetch('/api/content/ads-refresh', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
  const j = await r.json();
  if (!r.ok || j.error) return { ok: false, error: j.error ?? `HTTP ${r.status}` };
  return { ok: true, campaigns_synced: j.campaigns_synced, ads_synced: j.ads_synced };
}
